<template>
    <IonHeader
        class="ion-no-border"
        data-cy="filters-modal"
    >
        <div class="relative w-full font-medium text-md transition-colors duration-300 py-2">
            <div class="w-full flex items-center justify-between px-3 overflow-hidden">
                <div :class="{ 'w-[60px] shrink': isIos }">
                    <DButton
                        theme="transparent"
                        class="w-[32px] h-[38px] flex items-center justify-center rounded-full"
                        data-cy="filters-modal-cancel"
                        @click="cancel"
                    >
                        <PhIcon
                            v-if="!expandedSource"
                            icon="x"
                            weight="light"
                            :size="24"
                        />
                        <PhIcon
                            v-else-if="isIos"
                            icon="caret-left"
                            weight="regular"
                            :size="24"
                        />
                        <PhIcon
                            v-else
                            icon="arrow-left"
                            weight="regular"
                            :size="24"
                        />
                    </DButton>
                </div>

                <div class="w-full h-10 flex items-center px-3 overflow-hidden">
                    <span
                        class="flex-1 truncate text-gray-900 font-medium"
                        :class="isIos ? 'text-center' : 'text-left'"
                    >
                        {{ headerTitle }}
                    </span>
                </div>

                <div class="w-[60px] flex items-center justify-end">
                    <DButton
                        v-if="expandedSource"
                        theme="text-link"
                        :link-underline="false"
                        size="xs"
                        :disabled="!canClearFilter"
                        data-cy="filters-modal-clear-filter"
                        @click="clearFilter"
                    >
                        Clear
                    </DButton>
                    <DButton
                        v-else
                        theme="text-link"
                        :link-underline="false"
                        size="xs"
                        :disabled="!canClearAll"
                        data-cy="filters-modal-clear-all"
                        @click="clearAll"
                    >
                        Clear all
                    </DButton>
                </div>
            </div>
        </div>
    </IonHeader>
    <IonContent>
        <DForm
            v-if="expandedSource"
            class="pt-4"
        >
            <div
                v-if="displaySearch"
                class="flex items-center justify-between px-4 shrink-0"
            >
                <DField id="Search">
                    <DInput
                        v-model="search"
                        left-icon="magnifying-glass"
                        placeholder="Search"
                        aria-label="Search"
                        rounded
                        use-clickable-icon
                        @click-icon="resetSearch"
                    />
                </DField>
            </div>

            <div
                v-if="!search.length"
                class="pl-4 pr-4 flex items-center justify-between"
                @click="toggleSource(expandedSource)"
            >
                <div class="content-md-ux-medium">
                    Select all
                </div>
                <div class="scale-[1.3]">
                    <DCheckbox
                        v-model="sourcesSelectAll[expandedSource.id]"
                        @update:model-value="sourceChange(expandedSource)"
                    />
                </div>
            </div>
            <template
                v-for="(filter) in filterResults"
                :key="filter.id"
            >
                <div
                    class="pl-4 pr-4 flex items-center justify-between"
                    @click="toggleFilter(expandedSource, filter)"
                >
                    <div>
                        {{ filter.title }}
                    </div>
                    <div class="scale-[1.3]">
                        <DCheckbox
                            v-model="activeFiltersByFilterParam[expandedSource.filters_param][filter.id]"
                            @update:model-value="updateSourcesSelectAllValue(expandedSource)"
                        />
                    </div>
                </div>
            </template>
        </DForm>
        <DForm
            v-else
            class="pt-4"
        >
            <DField>
                <div class="font-medium text-lg ml-4 mt-1 mb-4">
                    View
                </div>
                <div class="ml-4 flex items-center gap-x-2">
                    <DChip
                        v-if="supportedViews.includes(VIEW_MONTH)"
                        :selected="selectedView === VIEW_MONTH"
                        left-icon="ph:calendar-dots"
                        @click="selectedView = VIEW_MONTH"
                    >
                        Month
                    </DChip>
                    <DChip
                        v-if="supportedViews.includes(VIEW_DAY)"
                        :selected="selectedView === VIEW_DAY"
                        left-icon="ph:calendar"
                        @click="selectedView = VIEW_DAY"
                    >
                        Day
                    </DChip>
                    <DChip
                        v-if="supportedViews.includes(VIEW_WEEK)"
                        :selected="selectedView === VIEW_WEEK"
                        left-icon="ph:rows"
                        @click="selectedView = VIEW_WEEK"
                    >
                        Week
                    </DChip>
                </div>
            </DField>
            <div
                v-if="sources.length"
                class="py-2 pt-0"
            >
                <DField>
                    <div
                        v-for="(groupSources, group) in groupedSources"
                        :key="group"
                        class="mb-4"
                    >
                        <div class="w-full h-2 surface-secondary" />
                        <div
                            v-if="group"
                            class="font-medium text-md ml-4 my-4"
                        >
                            {{ group }}
                        </div>
                        <div
                            v-for="(source, sourceIndex) in groupSources"
                            :key="source?.id"
                            class="px-4"
                        >
                            <div
                                class="min-h-11 flex items-center justify-between my-4"
                                @click="clickSource(source, group, sourceIndex)"
                            >
                                <div class="flex">
                                    <UIIcon
                                        :icon="source.icon"
                                        weight="regular"
                                        :size="24"
                                        class="mr-4 text-(--color-brand-700)"
                                    />
                                    <div>
                                        <div
                                            class="flex font-medium"
                                            data-cy="modal-calendar-source-title"
                                        >
                                            {{ source?.title }}
                                        </div>
                                        <div
                                            v-if="selectedCounts[source.filters_param]"
                                            class="text-neutral-extra-subtle text-sm mr-3"
                                        >
                                            {{ selectedCounts[source.filters_param] }} selected
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="!source.filters?.length"
                                    class="flex items-center"
                                >
                                    <DSwitch
                                        v-model="sourcesSelectAll[source.id]"
                                        class="scale-110"
                                        @update:model-value="sourceChange(source)"
                                    />
                                </div>
                                <PhIcon
                                    v-else
                                    icon="caret-right"
                                    class="text-neutral-extra-strong"
                                    :size="20"
                                    weight="regular"
                                />
                            </div>
                        </div>
                    </div>
                </DField>
            </div>
        </DForm>
    </IonContent>
    <IonFooter class="ion-no-border border-t border-neutral-base border-solid">
        <div class="flex w-full justify-stretch p-3">
            <DButton
                data-cy="filters-modal-confirm"
                wide
                class="mx-2"
                @click="confirm"
            >
                Apply
            </DButton>
        </div>
    </IonFooter>
</template>
<script setup>
    import {
        DForm, DButton, DField, DCheckbox, DChip, DSwitch, DInput,
    } from '@digistorm/spark'
    import { modalController } from '@ionic/vue'
    import UIIcon from '@/components/ui/UIIcon.vue'
    import { useCalendarHelpers } from '@/composables/calendarHelpers'
    import PhIcon from '@/components/app/PhIcon.vue'

    const props = defineProps({
        title: String,
        sources: Array,
        currentView: String,
        supportedViews: Array,
        currentParams: Object,
        applyChanges: Function,
    })

    const calendarHelpers = useCalendarHelpers()
    const deviceStore = useDeviceStore()

    const {
        VIEW_MONTH,
        VIEW_WEEK,
        VIEW_DAY,
    } = calendarHelpers

    const { isIos } = storeToRefs(deviceStore)

    const sourcesSelectAll = ref({})
    // Boolean for active status of filter keyed by filter id and grouped by filter param
    // E.g. { athletics_categories: { 52: true, 19: false }, events_categories: { 19: true }, }
    const activeFiltersByFilterParam = ref({})
    const expandedSourceKeys = ref(null)
    const selectedView = ref(props.currentView)
    const search = ref('')

    const groupedSources = computed(() => {
        return groupBy(props.sources, (source) => source?.group || '')
    })

    const expandedSource = computed(() => {
        const keys = expandedSourceKeys.value
        if (!keys) {
            return null
        }

        const groupSources = groupedSources.value?.[keys.group]
        if (!Array.isArray(groupSources)) {
            return null
        }

        const index = keys.sourceIndex
        if (typeof index !== 'number' || index < 0 || index >= groupSources.length) {
            return null
        }

        return groupSources[index] ?? null
    })

    const headerTitle = computed(() => {
        if (expandedSource.value) {
            return expandedSource.value.title
        }
        return props.title
    })

    const hasActiveFilters = computed(() => {
        return find(activeFiltersByFilterParam.value, (ids, param) => find(ids, (isActive, id) => isActive === true)) !== undefined
    })

    const canClearAll = computed(() => {
        return hasActiveFilters.value || find(sourcesSelectAll.value, (isSelected) => isSelected === true) !== undefined
    })

    const canClearFilter = computed(() => {
        if (!expandedSource.value) {
            return false
        }
        const filtersParam = expandedSource.value.filters_param
        if (!filtersParam) {
            return false
        }
        const filtersForParam = activeFiltersByFilterParam.value[filtersParam]
        if (!filtersForParam) {
            return false
        }
        return find(filtersForParam, (isActive) => isActive === true) !== undefined
    })

    const selectedCounts = computed(() => {
        return mapValues(activeFiltersByFilterParam.value, (sourceFilters, key) => {
            return reduce(sourceFilters, (result, value) => result + (value ? 1 : 0), 0)
        })
    })

    // List of sources that have any filters enabled
    const enabledSources = computed(() => {
        return map(
            filter(props.sources, (source) => selectedCounts.value[source.filters_param] || sourcesSelectAll.value[source.id]),
            'id',
        )
    })

    const displaySearch = computed(() => {
        return expandedSource.value && expandedSource.value.filters.length > 5
    })

    const filterResults = computed(() => {
        if (!displaySearch.value || !expandedSource.value?.filters) {
            return expandedSource.value.filters
        }
        const searchTerm = search.value.toLowerCase()
        return filter(expandedSource.value.filters, (sourceFilter) => sourceFilter.title.toLowerCase().includes(searchTerm))
    })

    const resetSearch = () => {
        search.value = ''
    }

    const sourceChange = (source) => {
        if (source.filters_param) {
            forEach(source.filters, (sourceFilter) => {
                activeFiltersByFilterParam.value[source.filters_param][sourceFilter.id] = sourcesSelectAll.value[source.id]
            })
        }
    }

    const updateSourcesSelectAllValue = (source) => {
        const previouslySelected = sourcesSelectAll.value[source.id]
        const hasFilters = source.filters.length > 0
        let allEnabled = true
        let someEnabled = false
        source.filters.forEach((sourceFilter) => {
            if (activeFiltersByFilterParam.value[source.filters_param][sourceFilter.id]) {
                someEnabled = true
            } else {
                // Disable "All" checkbox if any filter is unchecked
                allEnabled = false
            }
        })
        sourcesSelectAll.value[source.id] = (someEnabled && allEnabled) || (previouslySelected && !hasFilters)
    }

    const addMissingFilters = () => {
        forEach(props.sources, (source) => {
            if (source.filters_param) {
                if (!activeFiltersByFilterParam.value[source.filters_param]) {
                    activeFiltersByFilterParam.value[source.filters_param] = {}
                }
                updateSourcesSelectAllValue(source)
            }
        })
    }

    const resetFilters = () => {
        activeFiltersByFilterParam.value = {}
        sourcesSelectAll.value = {}
        addMissingFilters()
    }

    const toggleSource = (source) => {
        sourcesSelectAll.value[source.id] = !sourcesSelectAll.value[source.id]
        sourceChange(source)
    }

    const clickSource = (source, group, sourceIndex) => {
        if (source.filters?.length) {
            expandedSourceKeys.value = { group, sourceIndex }
        } else {
            toggleSource(source)
        }
    }

    const toggleFilter = (source, filter) => {
        activeFiltersByFilterParam.value[source.filters_param][filter.id] = !activeFiltersByFilterParam.value[source.filters_param][filter.id]
        updateSourcesSelectAllValue(source)
    }

    const cancel = () => {
        if (expandedSource.value) {
            expandedSourceKeys.value = null
            resetSearch()
        } else {
            modalController.dismiss(null, 'cancel')
        }
    }

    const confirm = () => {
        props.applyChanges(
            selectedView.value,
            {
                sources: enabledSources.value,
                ...mapValues(activeFiltersByFilterParam.value, (sourceFilters) => filter(map(sourceFilters, (value, key) => (value ? key : null)))),
            },
        )
        modalController.dismiss(null, 'confirm')
    }

    const clearAll = () => {
        resetFilters()
    }

    const clearFilter = () => {
        if (expandedSource.value) {
            const filtersForParam = activeFiltersByFilterParam.value[expandedSource.value.filters_param]

            if (!filtersForParam) {
                return
            }

            forEach(filtersForParam, (value, key) => {
                filtersForParam[key] = false
            })
            updateSourcesSelectAllValue(expandedSource.value)
        }
    }

    const initialiseFilters = () => {
        forEach(props.currentParams.sources, (source) => {
            sourcesSelectAll.value[source] = true
        })
        forEach(props.currentParams, (items, key) => {
            if (key === 'sources') {
                return
            }
            activeFiltersByFilterParam.value[key] = {}
            forEach(items, (item) => {
                activeFiltersByFilterParam.value[key][item] = true
            })
        })
        addMissingFilters()
    }

    initialiseFilters()
</script>
